import { FileDownload } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Typography } from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { currencies, getPriceWithCurrencyString } from '../../../main-app/utils/currencies';
import { formatDate } from '../../../main-app/utils/format';
import { fromCentesimal } from '../../../main-app/utils/transform';
import { useLeadInvoiceQuery } from '../../api/gql';
import { LeadInvoicePdfDocument } from './LeadInvoicePdfDocument';

type Props = {
  invoiceId: string;
};
export const LeadInvoiceDownloadButton = ({ invoiceId }: Props) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useLeadInvoiceQuery(
    {
      id: invoiceId ?? '',
    },

    {
      select: (res) => ({
        ...res.leadInvoice,
        amount: getPriceWithCurrencyString(fromCentesimal(res.leadInvoice.amount ?? 0), res.leadInvoice.currency),
        amountWithoutVat: getPriceWithCurrencyString(
          fromCentesimal(res.leadInvoice.amountWithoutVat ?? 0),
          res.leadInvoice.currency,
        ),
        amountVat: getPriceWithCurrencyString(fromCentesimal(res.leadInvoice.amountVat ?? 0), res.leadInvoice.currency),
        number: res.leadInvoice.number ?? '-',
        currency: currencies.find((curr) => curr.value === res.leadInvoice.currency?.toLowerCase())?.label ?? '-',
        issueDate: formatDate(new Date(res.leadInvoice.issueDate)) ?? '-',
        dueDate: formatDate(new Date(res.leadInvoice.dueDate)) ?? '-',
        taxPoint: res.leadInvoice.taxPoint ? formatDate(new Date(res.leadInvoice.taxPoint)) : '-',
        bankAccount: res.leadInvoice.bankAccount ?? '-',
        bankNumber: res.leadInvoice.bankNumber ?? '-',
        swift: res.leadInvoice.swift ?? '-',
        iban: res.leadInvoice.iban ?? '-',
        variableSymbol: res.leadInvoice.variableSymbol ?? '-',
        items:
          res.leadInvoice.items.map((item) => ({
            ...item,
            pricePerUnit: getPriceWithCurrencyString(fromCentesimal(item.pricePerUnit ?? 0), res.leadInvoice.currency),
            priceSum: getPriceWithCurrencyString(fromCentesimal(item.priceSum ?? 0), res.leadInvoice.currency),
            priceWithoutVat: getPriceWithCurrencyString(
              fromCentesimal(item.priceWithoutVat ?? 0),
              res.leadInvoice.currency,
            ),
            vatRate: item.vatRate ?? '-',
          })) ?? [],
        customer: {
          ...res.leadInvoice.customer,
          name: res.leadInvoice.customer.name ?? '-',
        },
        vendor: {
          ...res.leadInvoice.vendor,
          vatId: res.leadInvoice.vendor.vatId ?? '-',
          name: res.leadInvoice.vendor.name ?? '-',
          address: res.leadInvoice.vendor.address ?? '-',
          registry: res.leadInvoice.vendor.registry ?? '-',
        },
      }),
    },
  );

  if (error) {
    return (
      <Typography color={'error'}>Nepodařilo se načíst data faktury. Zkuste to, prosím, za chvíli znovu.</Typography>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <PDFDownloadLink document={<LeadInvoicePdfDocument data={data} />} fileName={`upominej-faktura-${data.number}.pdf`}>
      {({ loading }) => (
        <LoadingButton size={'large'} startIcon={<FileDownload />} variant={'contained'} loading={loading || isLoading}>
          {t('invoice.download')}
        </LoadingButton>
      )}
    </PDFDownloadLink>
  );
};
